"use client"

import { useState, useEffect, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { ArrowRight, Brain, Database, Clock, Target, ChevronRight, Loader2 } from "lucide-react"
import Link from "next/link"
import { createBrowserClient } from "@/lib/supabase/client"

// ============================================
// TYPES
// ============================================

interface BrainBriefData {
  brief_text: string
  org_name: string
  total_memories: number
  domain_count: number
  last_updated: string | null
  active_goal: string | null
  top_gaps: string[]
  computed_at: string
}

interface RecentChange {
  id: string
  key: string
  label: string
  domainSlug: string
  updatedAt: Date
}

interface HybridCard {
  id: string
  title: string
  whyItMatters: string
  nextMemoryKey: string
  guidedIntakeLink: string
  addMemoryLink: string
}

// ============================================
// OUTLINE PILL BUTTON STYLE (matches login Sign in button)
// Phase 1: All CTAs use rounded-full pill-outline style
// No saturated blue fill - subdued border with transparent bg
// ============================================

const outlinePillStyle = {
  background: "transparent",
  borderColor: "rgba(255,255,248,0.10)",
  color: "#F2F5F8",
}

// ============================================
// MAIN COMPONENT
// ============================================

export function BrainSnapshot() {
  const [isLoading, setIsLoading] = useState(true)
  const [briefData, setBriefData] = useState<BrainBriefData | null>(null)
  const [recentChanges, setRecentChanges] = useState<RecentChange[]>([])
  const [hybridCards, setHybridCards] = useState<HybridCard[]>([])
  const [visibleChanges, setVisibleChanges] = useState(5)
  const [loadingMore, setLoadingMore] = useState(false)

  // Fetch brain brief from API
  const fetchBrief = useCallback(async () => {
    try {
      const response = await fetch("/api/brain/brief")
      if (response.ok) {
        const data = await response.json()
        setBriefData(data)
        return data
      }
    } catch (error) {
      console.error("[BrainSnapshot] Failed to fetch brief:", error)
    }
    // Fallback data
    return {
      brief_text: "As you approve memories, your Business Brain becomes more specific. Start by telling me about your business and what you're working toward.",
      org_name: "Your business",
      total_memories: 0,
      domain_count: 0,
      last_updated: null,
      active_goal: null,
      top_gaps: ["identity", "audience", "offerings"],
      computed_at: new Date().toISOString(),
    }
  }, [])

  // Fetch recent changes
  const fetchRecentChanges = useCallback(async () => {
    try {
      const supabase = createBrowserClient()
      const { data: memories } = await supabase
        .from("memories")
        .select("id, key, label, updated_at")
        .eq("is_archived", false)
        .order("updated_at", { ascending: false })
        .limit(20)

      if (memories) {
        setRecentChanges(memories.map(m => ({
          id: m.id,
          key: m.key,
          label: m.label || m.key,
          domainSlug: m.key?.split(".")[0] || "unknown",
          updatedAt: new Date(m.updated_at),
        })))
      }
    } catch (error) {
      console.error("[BrainSnapshot] Failed to fetch changes:", error)
    }
  }, [])

  // Generate hybrid cards based on gaps and context (Phase 4: simplified cards)
  const generateHybridCards = useCallback((briefData: BrainBriefData): HybridCard[] => {
    const cards: HybridCard[] = []
    const hasGoal = !!briefData.active_goal

    // Card 1: Identity Profile (always first if gaps exist)
    if (briefData.top_gaps.includes("identity") || briefData.total_memories < 5) {
      cards.push({
        id: "identity-profile",
        title: "Identity",
        whyItMatters: hasGoal 
          ? `Your advisor needs profile basics to give relevant advice for "${briefData.active_goal}".`
          : "Grounds all recommendations in who you actually are.",
        nextMemoryKey: "identity.basics.preferred_name",
        guidedIntakeLink: "/advisor?intake=1&domain=identity&node=basics&key=preferred_name",
        addMemoryLink: "/brain?scope=node:identity/basics&addMemory=true&key=preferred_name",
      })
    }

    // Card 2: Mission (if not set)
    if (briefData.top_gaps.includes("identity") || !briefData.total_memories) {
      cards.push({
        id: "mission",
        title: "Mission",
        whyItMatters: hasGoal
          ? "A clear mission helps evaluate every decision against your goal."
          : "Defines your purpose and helps prioritize what matters.",
        nextMemoryKey: "identity.mission.statement",
        guidedIntakeLink: "/advisor?intake=1&domain=identity&node=mission&key=statement",
        addMemoryLink: "/brain?scope=node:identity/mission&addMemory=true&key=statement",
      })
    }

    // Card 3: Audience or Strategy (contextual)
    if (hasGoal && briefData.top_gaps.includes("strategy")) {
      cards.push({
        id: "strategy",
        title: "Strategy",
        whyItMatters: "Helps your advisor give specific execution guidance for your 90-day goal.",
        nextMemoryKey: "strategy.positioning",
        guidedIntakeLink: "/advisor?intake=1&domain=strategy&key=positioning",
        addMemoryLink: "/brain?scope=domain:strategy&addMemory=true&key=positioning",
      })
    } else if (briefData.top_gaps.includes("audience")) {
      cards.push({
        id: "audience",
        title: "Audience",
        whyItMatters: "Understanding who you serve improves advice quality for growth.",
        nextMemoryKey: "audience.primary",
        guidedIntakeLink: "/advisor?intake=1&domain=audience&key=primary",
        addMemoryLink: "/brain?scope=domain:audience&addMemory=true&key=primary",
      })
    } else if (briefData.top_gaps.includes("offerings")) {
      cards.push({
        id: "offerings",
        title: "Offerings",
        whyItMatters: "Helps your advisor understand what you sell and how you create value.",
        nextMemoryKey: "offerings.primary",
        guidedIntakeLink: "/advisor?intake=1&domain=offerings&key=primary",
        addMemoryLink: "/brain?scope=domain:offerings&addMemory=true&key=primary",
      })
    }

    return cards.slice(0, 3)
  }, [])

  // Initial load
  useEffect(() => {
    async function loadData() {
      setIsLoading(true)
      const brief = await fetchBrief()
      setBriefData(brief)
      setHybridCards(generateHybridCards(brief))
      await fetchRecentChanges()
      setIsLoading(false)
    }
    loadData()
  }, [fetchBrief, fetchRecentChanges, generateHybridCards])

  // Load more changes (Phase 5: pagination)
  const handleLoadMore = async () => {
    setLoadingMore(true)
    setVisibleChanges(prev => Math.min(prev + 5, recentChanges.length))
    setLoadingMore(false)
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-12 w-64 bg-muted/30 rounded" />
        <div className="h-6 w-48 bg-muted/20 rounded" />
        <div className="h-24 bg-muted/20 rounded-lg" />
        <div className="h-32 bg-muted/20 rounded-lg" />
      </div>
    )
  }

  const displayData = briefData || {
    brief_text: "As you approve memories, your Business Brain becomes more specific.",
    org_name: "Your business",
    total_memories: 0,
    domain_count: 0,
    last_updated: null,
    active_goal: null,
    top_gaps: [],
    computed_at: new Date().toISOString(),
  }

  return (
    <div className="space-y-10">
      {/* ========================================
          Phase 6: Header Block (H1 larger + H2 org name + Brief + CTAs)
          ======================================== */}
      <div className="space-y-4">
        {/* H1: Business Brain (larger than before) */}
        <h1 className="text-4xl md:text-5xl font-display font-medium text-foreground tracking-tight">
          Business Brain
        </h1>
        
        {/* H2: Org display name (uses identity.basics.preferred_name) */}
        <h2 className="text-xl md:text-2xl text-muted-foreground font-sans">
          {displayData.org_name}
        </h2>
        
        {/* Personalized brief (Phase 6: dynamic based on memories/gaps/goal) */}
        <p className="text-base text-muted-foreground/80 leading-relaxed max-w-2xl line-clamp-5">
          {displayData.brief_text}
        </p>

        {/* CTA Row - Phase 1: All pills, outline style */}
        <div className="flex items-center gap-4 pt-2">
          {/* Primary: Talk with Advisor (outline pill) */}
          <Link href="/advisor?new=1&from=brain">
            <Button
              variant="outline"
              className="h-10 px-5 rounded-full bg-transparent hover:bg-white/5 gap-2"
              style={outlinePillStyle}
            >
              Talk with Advisor
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          
          {/* Secondary: Review recent changes (ghost/text) */}
          {recentChanges.length > 0 && (
            <Button
              variant="ghost"
              className="h-10 px-4 rounded-full text-muted-foreground hover:text-foreground"
              onClick={() => document.getElementById("recent-changes")?.scrollIntoView({ behavior: "smooth" })}
            >
              Review recent changes
            </Button>
          )}
        </div>
      </div>

      {/* ========================================
          Phase 2: Knowledge Snapshot (section label + 4 tiles, no outer box)
          ======================================== */}
      <div className="space-y-4">
        {/* Section label (matches MOST USEFUL NEXT style) */}
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
          Knowledge Snapshot
        </h3>

        {/* 4 stat tiles in a row (no outer container card) */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {/* Tile 1: Memories */}
          <div className="text-center p-4 rounded-xl border border-[var(--border)] bg-[var(--surface)]">
            <div className="flex items-center justify-center mb-2">
              <Database className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-display font-medium text-foreground">{displayData.total_memories}</div>
            <div className="text-xs text-muted-foreground mt-1">Memories</div>
          </div>

          {/* Tile 2: Domains */}
          <div className="text-center p-4 rounded-xl border border-[var(--border)] bg-[var(--surface)]">
            <div className="flex items-center justify-center mb-2">
              <Brain className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-2xl font-display font-medium text-foreground">{displayData.domain_count}</div>
            <div className="text-xs text-muted-foreground mt-1">Domains</div>
          </div>

          {/* Tile 3: Last updated */}
          <div className="text-center p-4 rounded-xl border border-[var(--border)] bg-[var(--surface)]">
            <div className="flex items-center justify-center mb-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-lg font-display font-medium text-foreground">
              {displayData.last_updated ? formatRelativeTime(new Date(displayData.last_updated)) : "—"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Last updated</div>
          </div>

          {/* Tile 4: Active goal */}
          <div className="text-center p-4 rounded-xl border border-[var(--border)] bg-[var(--surface)]">
            <div className="flex items-center justify-center mb-2">
              <Target className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="text-lg font-display font-medium text-foreground">
              {displayData.active_goal ? "Yes" : "No"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Active goal</div>
          </div>
        </div>
      </div>

      {/* ========================================
          Phase 4: Most Useful Next (simplified cards)
          - No yellow status icon
          - No "Open section →"
          - Keep: Title, why-it-matters, Next: key, pill buttons
          ======================================== */}
      {hybridCards.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
            Most useful next
          </h3>
          <div className="space-y-3">
            {hybridCards.map((card) => (
              <div
                key={card.id}
                className="p-4 rounded-xl border border-[var(--border)] bg-[var(--surface)] hover:border-[var(--brain)]/30 transition-colors"
              >
                {/* Title */}
                <div className="text-sm font-medium text-foreground">{card.title}</div>
                
                {/* Why it matters */}
                <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {card.whyItMatters}
                </div>
                
                {/* Next memory suggestion (mono, muted) */}
                <div className="text-xs text-muted-foreground/60 mt-2 font-mono">
                  Next: {card.nextMemoryKey}
                </div>

                {/* Actions - Phase 1: All pill buttons */}
                <div className="flex items-center gap-2 mt-3">
                  {/* Primary: Guided intake (outline pill) */}
                  <Link href={card.guidedIntakeLink}>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-8 text-xs rounded-full bg-transparent"
                      style={outlinePillStyle}
                    >
                      Guided intake
                    </Button>
                  </Link>
                  
                  {/* Secondary: Add this memory (ghost pill) */}
                  <Link href={card.addMemoryLink}>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 text-xs rounded-full"
                    >
                      Add this memory
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========================================
          Phase 5: Recent Changes (with pagination + footer)
          ======================================== */}
      {recentChanges.length > 0 && (
        <div id="recent-changes" className="space-y-4">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
            Recent changes
          </h3>
          <div className="space-y-2">
            {recentChanges.slice(0, visibleChanges).map((change) => (
              <Link
                key={change.id}
                href={`/brain?memory=${change.id}`}
                className="flex items-center justify-between p-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] hover:border-[var(--brain)]/30 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{change.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {formatRelativeTime(change.updatedAt)}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </Link>
            ))}
          </div>

          {/* Load more button (pill style) */}
          {visibleChanges < recentChanges.length && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="w-full h-9 text-xs rounded-full bg-transparent"
              style={outlinePillStyle}
            >
              {loadingMore ? (
                <>
                  <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "Load more"
              )}
            </Button>
          )}
        </div>
      )}

      {/* ========================================
          Phase 5: Footer (brand/legal, not instructional)
          ======================================== */}
      <footer className="pt-6 border-t border-[var(--border)]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-muted-foreground/60">
          <span>&copy; Logos Strategy Co. LLC</span>
          <div className="flex items-center gap-4">
            <Link href="/privacy" className="hover:text-foreground transition-colors">
              Privacy Policy
            </Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function formatRelativeTime(date: Date): string {
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}
